import {type Permission} from '@hconnect/apiclient'
import {NavItem} from '@hconnect/uikit/src/lib2'
import {FeatureFlagConfig, FeatureFlagKey, useFeatureFlag} from '@hproduce/feature-flags'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath, useParams} from 'react-router'

import {HROC_PREFIX} from '../../shared/consts'
import {
  hasChecklistsPermission,
  hasViewReliabilityEngineerDashboardPermission,
  hasViewProcessEngineerDashboardPermission,
  hasViewOptimizationSpecialistDashboardPermission,
  hasRcfaCompliancePagePermission,
  hasPerformanceReviewPermission,
  hasMajorOutageLeadDashboardPermission,
  hasSystemsEngineerDashboardPermission,
  hasServiceLevelAgreementTrackingPermission,
  hasJobsToBeDonePermission,
  hasRiskSurveyPermission
} from '../utils/permissions'

import {
  CHECKLISTS,
  OPTIMIZATION_SPECIALIST_DASHBOARD,
  PLANT_CHECKLISTS_TEMPLATES,
  PLANT_RELIABILITY_ENGINEER_DASHBOARD,
  RELIABILITY_ENGINEER_DASHBOARD,
  PROCESS_ENGINEER_DASHBOARD,
  PLANT_PROCESS_ENGINEER_DASHBOARD,
  PLANT_OPTIMIZATION_SPECIALIST_DASHBOARD,
  RCFA_COMPLIANCE,
  PERFORMANCE_REVIEW,
  MAJOR_OUTAGE_LEAD_DASHBOARD,
  PLANT_PERFORMANCE_REVIEW,
  PLANT_MAJOR_OUTAGE_LEAD_DASHBOARD,
  SYSTEMS_ENGINEER_DASHBOARD,
  SERVICE_LEVEL_AGREEMENT_DASHBOARD,
  PLANT_SYSTEMS_ENGINEER_DASHBOARD,
  PLANT_SERVICE_LEVEL_AGREEMENT_DASHBOARD,
  PLANT_JOBS_TO_BE_DONE_DASHBOARD,
  JOBS_TO_BE_DONE_DASHBOARD
} from './routes'

type PathParameter = {
  plantId?: string
}

// eslint-disable-next-line complexity
export const useGetHrocLinks = ({permissions}: {permissions: Permission[]}): NavItem[] => {
  const {plantId} = useParams<PathParameter>()
  const {t} = useTranslation()

  const userHasChecklistsPermission = useMemo(
    () => hasChecklistsPermission(permissions),
    [permissions]
  )
  const userHasReliabilityEngineerPermission = useMemo(
    () => hasViewReliabilityEngineerDashboardPermission(permissions),
    [permissions]
  )

  const userHasProcessEngineerPermission = useMemo(
    () => hasViewProcessEngineerDashboardPermission(permissions),
    [permissions]
  )

  const userHasOptimizationSpecialistPermission = useMemo(
    () => hasViewOptimizationSpecialistDashboardPermission(permissions),
    [permissions]
  )

  const userHasPerformanceReviewPermission = useMemo(
    () => hasPerformanceReviewPermission(permissions),
    [permissions]
  )

  const userMajorOutageLeadPermission = useMemo(
    () => hasMajorOutageLeadDashboardPermission(permissions),
    [permissions]
  )

  const userHasSystemsEngineerPermission = useMemo(
    () => hasSystemsEngineerDashboardPermission(permissions),
    [permissions]
  )

  const userHasServiceLevelAgreementTrackingPermission = useMemo(
    () => hasServiceLevelAgreementTrackingPermission(permissions),
    [permissions]
  )

  const userHasJobsToBeDonePermission = useMemo(
    () => hasJobsToBeDonePermission(permissions),
    [permissions]
  )

  const userHasRiskSurveyPermission = useMemo(
    () => hasRiskSurveyPermission(permissions),
    [permissions]
  )

  const isRiskSurveyEnabled = useFeatureFlag(FeatureFlagConfig.Suite, FeatureFlagKey.RiskSurvey)

  const checklistNavItem: NavItem = {
    url: plantId ? generatePath(PLANT_CHECKLISTS_TEMPLATES, {plantId}) : CHECKLISTS,
    label: t(`${HROC_PREFIX}.navPages.checklists`)
  }

  const reliabilityEngineerDashboardNavItem: NavItem = {
    url: plantId
      ? generatePath(PLANT_RELIABILITY_ENGINEER_DASHBOARD, {plantId})
      : RELIABILITY_ENGINEER_DASHBOARD,
    label: t(`${HROC_PREFIX}.navPages.reliabilityEngineerDashboard`),
    dataTestId: 'reliability-engineer-dashboard-nav-item'
  }

  const processEngineerDashboardNavItem: NavItem = {
    url: plantId
      ? generatePath(PLANT_PROCESS_ENGINEER_DASHBOARD, {plantId})
      : PROCESS_ENGINEER_DASHBOARD,
    label: t(`${HROC_PREFIX}.navPages.processEngineerDashboard`),
    dataTestId: 'process-engineer-dashboard-nav-item'
  }

  const optimizationSpecialistDashboardNavItem: NavItem = {
    url: plantId
      ? generatePath(PLANT_OPTIMIZATION_SPECIALIST_DASHBOARD, {plantId})
      : OPTIMIZATION_SPECIALIST_DASHBOARD,
    label: t(`${HROC_PREFIX}.navPages.optimizationSpecialistDashboard`),
    dataTestId: 'optimization-specialist-dashboard-nav-item'
  }

  const performanceReviewNavItem: NavItem = {
    url: plantId ? generatePath(PLANT_PERFORMANCE_REVIEW, {plantId}) : PERFORMANCE_REVIEW,
    label: t(`${HROC_PREFIX}.navPages.performanceReview`),
    dataTestId: 'performance-review-nav-item'
  }

  const majorOutageLeadNavItem: NavItem = {
    url: plantId
      ? generatePath(PLANT_MAJOR_OUTAGE_LEAD_DASHBOARD, {plantId})
      : MAJOR_OUTAGE_LEAD_DASHBOARD,
    label: t(`${HROC_PREFIX}.navPages.majorOutageLeadDashboard`),
    dataTestId: 'major-outage-lead-nav-item'
  }

  const systemsEngineerNavItem: NavItem = {
    url: plantId
      ? generatePath(PLANT_SYSTEMS_ENGINEER_DASHBOARD, {plantId})
      : SYSTEMS_ENGINEER_DASHBOARD,
    label: t(`${HROC_PREFIX}.navPages.systemsEngineerDashboard`),
    dataTestId: 'systems-engineer-nav-item'
  }

  const serviceLevelAgreementTrackingNavItem: NavItem = {
    url: plantId
      ? generatePath(PLANT_SERVICE_LEVEL_AGREEMENT_DASHBOARD, {plantId})
      : SERVICE_LEVEL_AGREEMENT_DASHBOARD,
    label: t(`${HROC_PREFIX}.navPages.serviceLevelAgreementTrackingDashboard`),
    dataTestId: 'sla-tracking-nav-item'
  }

  const jobsToBeDoneNavItem: NavItem = {
    url: plantId
      ? generatePath(PLANT_JOBS_TO_BE_DONE_DASHBOARD, {plantId})
      : JOBS_TO_BE_DONE_DASHBOARD,
    label: t(`${HROC_PREFIX}.navPages.jobsToBeDoneDashboard`),
    dataTestId: 'jobs-to-be-done-nav-item'
  }

  const riskSurveyNavItem: NavItem = {
    url: '/risk-survey',
    label: t(`${HROC_PREFIX}.navPages.riskSurveyDashboard`),
    dataTestId: 'risk-survey-nav-item'
  }

  const navItems: NavItem[] = []

  if (userHasChecklistsPermission) {
    navItems.push(checklistNavItem)
  }
  if (userHasReliabilityEngineerPermission) {
    navItems.push(reliabilityEngineerDashboardNavItem)
  }
  if (userHasProcessEngineerPermission) {
    navItems.push(processEngineerDashboardNavItem)
  }
  if (userHasOptimizationSpecialistPermission) {
    navItems.push(optimizationSpecialistDashboardNavItem)
  }

  if (userHasPerformanceReviewPermission) {
    navItems.push(performanceReviewNavItem)
  }

  if (userMajorOutageLeadPermission) {
    navItems.push(majorOutageLeadNavItem)
  }

  if (userHasSystemsEngineerPermission) {
    navItems.push(systemsEngineerNavItem)
  }

  if (userHasServiceLevelAgreementTrackingPermission) {
    navItems.push(serviceLevelAgreementTrackingNavItem)
  }

  if (userHasJobsToBeDonePermission) {
    navItems.push(jobsToBeDoneNavItem)
  }

  if (userHasRiskSurveyPermission && isRiskSurveyEnabled) {
    navItems.push(riskSurveyNavItem)
  }

  return navItems
}

// It was requested for RCFA compliance to be always visible in the top nav so it is handled separately
export const useRcfaComplianceNavItem = ({
  permissions
}: {
  permissions: Permission[]
}): NavItem | null => {
  const {t} = useTranslation()

  const rfcaComplianceNavItem: NavItem = {
    url: RCFA_COMPLIANCE,
    label: t(`${HROC_PREFIX}.navPages.rcfaCompliance`),
    dataTestId: 'rcfa-compliance-nav-item'
  }

  const userHasRcfaCompliancePermission = useMemo(
    () => hasRcfaCompliancePagePermission(permissions),
    [permissions]
  )

  if (userHasRcfaCompliancePermission) {
    return rfcaComplianceNavItem
  }

  return null
}
